import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { BarChart, costTone, type Bar } from '@/components/charts/BarChart';
import { ChartEmpty, ChartFrame, ChartSkeleton } from '@/components/charts/ChartFrame';
import { DistributionBar } from '@/components/charts/DistributionBar';
import { LineChart } from '@/components/charts/LineChart';
import { Avatar } from '@/components/brand/Brand';
import { ButtonLink } from '@/components/ui/Button';
import { EmptyState, InsightCard, Skeleton } from '@/components/ui/Feedback';
import { Pill, PillGroup } from '@/components/ui/Pill';
import { SectionBar } from '@/components/ui/SectionBar';
import { StatBlock, StatRow } from '@/components/ui/StatBlock';
import { useMenu } from '@/components/ui/useMenu';
import { HandicapChart, ToParChart } from './charts';
import {
  statsEligibleRounds,
  courseNameFrom,
  useAllRounds,
  useCourse,
  useCourseIndex,
  useRoundDetails,
} from '@/lib/api/queries';
import type { Round, RoundSummary } from '@/lib/api/types';
import { useAuth } from '@/lib/auth/AuthContext';
import { cx } from '@/lib/cx';
import { fmtDateShort } from '@/lib/format/date';
import { useIsDesktop } from '@/lib/useMediaQuery';
import { fmtDecimal, fmtDecimal2, fmtInt, fmtPct, fmtToPar } from '@/lib/format/number';
import {
  countingDifferentials,
  summariseRounds,
  withDetailStats,
} from '@/lib/stats/aggregate';
import { holeStatsFor, MIN_ROUNDS_FOR_HOLE_INSIGHT } from '@/lib/stats/holeStats';
import {
  axisMonths,
  dateRangeLabel,
  differentialSeries,
  fairwaySeries,
  girSeries,
  parDiagnosis,
  performanceByPar,
  trendLine,
  worstHoles,
  MIN_ROUNDS_FOR_DIFFERENTIALS,
  MIN_ROUNDS_FOR_SERIES,
} from '@/lib/stats/series';
import { t, tx } from '@/lib/i18n';
import { routes } from '@/routes/paths';
import styles from './Stats.module.css';
import { ChevronDownIcon } from '@/components/icons';

/**
 * Stats detalhados (canvas 1l desktop, 1o mobile) — a tela dos gráficos, aberta pela cápsula
 * "Detalhados ▸" do resumo (1c). Antes era a própria aba Stats.
 *
 * There is no aggregate endpoint, so this screen is entirely derived: it pulls every round,
 * then pulls each one's detail for the fields the list projection omits (`stats`,
 * `playerHandicapAtStart`), and does the arithmetic in `lib/stats`. That N+1 is why the
 * rate-limit fix had to land first, and why the skeleton here is shaped like the real chart
 * and reports its progress — the canvas calls this "a maior espera do app".
 *
 * The rule that shapes the whole screen: **a chart that lacks the data to be honest draws
 * nothing and says why.** Every threshold is named in `lib/stats/series.ts`.
 */

type Window = 5 | 10 | 'all';
const ALL_COURSES = 'all';

export function Stats() {
  const { user } = useAuth();
  const allRounds = useAllRounds();
  const courseIndex = useCourseIndex();

  // The header flips surface at the breakpoint (green on mobile, white on desktop), and the
  // pills' contrast has to follow. That is a prop, not a style, so it needs the media query.
  const tone = useIsDesktop() ? 'default' : 'onGreen';

  const [window, setWindow] = useState<Window>('all');
  const [holes, setHoles] = useState<9 | 18 | null>(null);
  const [courseId, setCourseId] = useState<string>(ALL_COURSES);

  const completed = useMemo(
    () => statsEligibleRounds(allRounds.data?.rounds ?? []),
    [allRounds.data?.rounds],
  );

  /*
   * Which round counts are on offer. Mixing 9- and 18-hole rounds in one average is the
   * quickest way to make this screen lie — a 9-hole 45 next to an 18-hole 88 averages to
   * something that describes no round the player ever played — so exactly one count is
   * always selected. The control disappears entirely for a player who only plays 18.
   */
  const holeCounts = useMemo(() => {
    const present = new Set(completed.map((r) => r.holesPlayed));
    return ([18, 9] as const).filter((n) => present.has(n));
  }, [completed]);

  const activeHoles = holes && holeCounts.includes(holes) ? holes : (holeCounts[0] ?? 18);

  const coursesPlayed = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of completed) counts.set(r.courseId, (counts.get(r.courseId) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [completed]);

  const scoped = useMemo(
    () =>
      completed.filter(
        (r) =>
          r.holesPlayed === activeHoles && (courseId === ALL_COURSES || r.courseId === courseId),
      ),
    [completed, activeHoles, courseId],
  );

  // `completedRounds` is newest first; the window takes the most recent N, then charts read
  // them oldest-to-newest.
  const filtered = useMemo(
    () => (window === 'all' ? scoped : scoped.slice(0, window)),
    [scoped, window],
  );

  /*
   * Which differentials WHS is currently using, computed over the **whole** history rather
   * than the filtered view. Narrowing to the last 5 rounds must not re-mark different rounds
   * as counting — the index the server calculated used all of them.
   */
  const counting = useMemo(() => countingDifferentials(completed), [completed]);

  const details = useRoundDetails(filtered.map((r) => r.id));
  const detailsReady = filtered.length > 0 && details.loadedCount === filtered.length;

  /*
   * Charts wait for every detail before drawing. Partial data would render a handicap line
   * that redraws into a different shape as requests land — worse than a skeleton, because
   * the first version looks finished.
   */
  const progress = detailsReady
    ? undefined
    : t('stats.loading', { done: details.loadedCount, total: filtered.length });

  const summary = useMemo(
    () => withDetailStats(summariseRounds(filtered), detailsReady ? details.rounds : []),
    [filtered, detailsReady, details.rounds],
  );

  const best = useMemo(() => bestRound(filtered), [filtered]);

  // The per-hole panel needs the course's pars, so it fixes on one course: whichever is
  // selected, or the one the player has played the most.
  const focusCourseId = courseId !== ALL_COURSES ? courseId : coursesPlayed[0]?.[0];
  const focusCourse = useCourse(focusCourseId);
  const focusName = focusCourseId ? courseNameFrom(courseIndex.data, focusCourseId) : null;

  if (allRounds.isLoading) return <PageSkeleton />;

  if (completed.length === 0) {
    return (
      <div className={styles.page}>
        <EmptyState
          title={t('stats.empty.title')}
          actions={
            <ButtonLink to={routes.record} size="lg">
              {t('stats.empty.action')}
            </ButtonLink>
          }
        >
          {t('stats.empty.body')}
        </EmptyState>
      </div>
    );
  }

  const detailRounds = detailsReady ? details.rounds : [];

  return (
    <div className={styles.page}>
      {/*
        Header. Canvas 1o puts it on brand green with the avatar, 1l on white with neither —
        the desktop shell already carries the wordmark and avatar in its own bar, so
        repeating them there would be furniture.
      */}
      <header className={styles.header}>
        <div className={styles.headerBar}>
          <div>
            {/* "Stats detalhados" no canvas: o resumo é que se chama Stats, e esta é a tela
                que ele abre. Repetir "Estatísticas" faria as duas telas parecerem a mesma. */}
            <h1 className={styles.title}>{t('stats.title')}</h1>
            <p className={styles.subtitle}>{subtitleFor(filtered, activeHoles)}</p>
          </div>
          <Link to={routes.profile} className={styles.avatarSlot} aria-label={t('nav.aria.profile')}>
            <Avatar name={user?.displayName ?? ''} size={34} />
          </Link>
        </div>

        <div className={styles.filters}>
          <PillGroup label={t('stats.filtersAria')} scroll>
            {([5, 10, 'all'] as const).map((w) => (
              <Pill key={w} tone={tone} selected={window === w} onClick={() => setWindow(w)}>
                {w === 'all' ? t('stats.window.all') : w === 5 ? t('stats.window.last5') : '10'}
              </Pill>
            ))}

            {holeCounts.length > 1 && <span className={styles.divider} aria-hidden="true" />}

            {holeCounts.length > 1 ? (
              holeCounts.map((n) => (
                <Pill key={n} tone={tone} selected={activeHoles === n} onClick={() => setHoles(n)}>
                  {t('stats.holes', { count: n })}
                </Pill>
              ))
            ) : (
              <Pill tone={tone} selected>
                {t('stats.holes', { count: activeHoles })}
              </Pill>
            )}

            <CoursePicker
              courses={coursesPlayed.map(([id, count]) => ({
                id,
                count,
                name: courseNameFrom(courseIndex.data, id),
              }))}
              value={courseId}
              onChange={setCourseId}
            />
          </PillGroup>
        </div>
      </header>

      {filtered.length === 0 ? (
        <p className={styles.noMatch}>
          {courseId !== ALL_COURSES
            ? t('stats.noMatchCourse', { holes: activeHoles })
            : t('stats.noMatch', { holes: activeHoles })}
        </p>
      ) : (
        <>
          {/* ---------------------------------------------------------- resumo */}
          <SectionBar variant="solid">{t('stats.summary')}</SectionBar>
          <StatRow className={styles.summary}>
            <StatBlock
              label={t('stats.bestRound')}
              value={fmtInt(summary.bestScore)}
              caption={
                best && (
                  <Link to={routes.round(best.id)} className={styles.captionLink}>
                    {fmtDateShort(best.completedAt ?? best.startedAt)} ·{' '}
                    {courseNameFrom(courseIndex.data, best.courseId)}
                  </Link>
                )
              }
            />
            <StatBlock
              label={t('stats.averageScore')}
              value={fmtDecimal(summary.averageScore)}
              caption={
                summary.averageToPar != null
                  ? t('stats.toParCaption', {
                      value: fmtToPar(Math.round(summary.averageToPar)),
                    })
                  : undefined
              }
            />
            {/* The screen's one gold: the differential that is actually holding the index up. */}
            <StatBlock
              label={t('stats.bestDifferential')}
              value={fmtDecimal(summary.bestDifferential)}
              caption={t('stats.countsForHandicap')}
              gold
            />
            <StatBlock
              label={t('stats.averageGir')}
              value={detailsReady ? fmtPct(summary.averageGirPct) : null}
              pct={summary.averageGirPct}
            />
            <StatBlock
              label={t('stats.puttsPerRound')}
              value={detailsReady ? fmtDecimal(summary.averagePuttsPerRound) : null}
              caption={
                detailsReady && summary.averagePuttsPerHole != null
                  ? t('stats.puttsPerHole', {
                      value: fmtDecimal2(summary.averagePuttsPerHole),
                    })
                  : undefined
              }
            />
            <StatBlock
              label={t('stats.rounds')}
              value={`${filtered.length}`}
              caption={otherCountCaption(completed, activeHoles)}
            />
          </StatRow>

          {/* -------------------------------------------------------- evolução */}
          <SectionBar variant="solid">{t('stats.evolution')}</SectionBar>
          <div className={styles.grid}>
            <HandicapChart rounds={detailRounds} ready={detailsReady} progress={progress} />
            <DifferentialsChart rounds={filtered} counting={counting} />
            <ToParChart rounds={filtered} />
            <GirFairwayChart rounds={detailRounds} ready={detailsReady} progress={progress} />
          </div>

          {/* -------------------------------------------------------- padrões */}
          <SectionBar variant="solid">{t('stats.patterns')}</SectionBar>
          <div className={styles.patterns}>
            <DistributionPanel
              rounds={detailRounds}
              ready={detailsReady}
              progress={progress}
              courseIndex={courseIndex.data}
            />
            <ByParPanel rounds={detailRounds} ready={detailsReady} progress={progress} />
            <ByHolePanel
              course={focusCourse.data}
              courseName={focusName}
              rounds={detailRounds}
              ready={detailsReady && !focusCourse.isLoading}
              progress={progress}
            />
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================== evolução

function DifferentialsChart({
  rounds,
  counting,
}: {
  rounds: RoundSummary[];
  counting: Set<string>;
}) {
  const points = differentialSeries(rounds, counting);
  const values = points.map((p) => p.value);
  const trend = trendLine(values);

  return (
    <ChartFrame
      title={t('stats.differentials')}
      subtitle={t('stats.differentialsSub')}
      aside={
        points.length >= MIN_ROUNDS_FOR_DIFFERENTIALS ? (
          <>
            <span className={styles.legendItem}>
              <span className={cx(styles.legendDot, styles.legendDotGold)} aria-hidden="true" />
              {t('stats.countingLegend')}
            </span>
            <span className={styles.legendItem}>
              <span className={cx(styles.legendDot, styles.legendDotHollow)} aria-hidden="true" />
              {t('stats.discardedLegend')}
            </span>
          </>
        ) : undefined
      }
      xLabels={points.length >= MIN_ROUNDS_FOR_DIFFERENTIALS ? axisMonths(points) : undefined}
    >
      {points.length < MIN_ROUNDS_FOR_DIFFERENTIALS ? (
        <ChartEmpty
          shape="dots"
          requirement={t('stats.needCompletedRounds', { count: MIN_ROUNDS_FOR_DIFFERENTIALS })}
          have={t('stats.youHave', { count: points.length })}
        />
      ) : (
        <LineChart
          ariaLabel={t('stats.differentialsAria', {
            min: fmtDecimal(Math.min(...values)),
            max: fmtDecimal(Math.max(...values)),
          })}
          series={[
            ...(trend
              ? [
                  {
                    values: values.map((_, i) => trend(i)),
                    color: 'var(--dist-par)',
                    strokeWidth: 1.6,
                    dashed: true,
                  },
                ]
              : []),
            {
              // Dots only — the differentials are independent measurements, and joining
              // them with a line would imply a path between them that does not exist.
              values,
              color: 'transparent',
              strokeWidth: 0,
              dot: (i) =>
                points[i]?.counts
                  ? { r: 4.5, fill: 'var(--gold)' }
                  : { r: 4, fill: 'var(--canvas)', stroke: 'var(--text-muted)', strokeWidth: 1.4 },
            },
          ]}
        />
      )}
    </ChartFrame>
  );
}

function GirFairwayChart({
  rounds,
  ready,
  progress,
}: {
  rounds: Round[];
  ready: boolean;
  progress?: string;
}) {
  const gir = girSeries(rounds);
  const fairway = fairwaySeries(rounds);

  return (
    <ChartFrame
      title={t('stats.girFairway')}
      subtitle={t('stats.girFairwaySub')}
      aside={
        gir.length >= MIN_ROUNDS_FOR_SERIES ? (
          <>
            <span className={styles.legendItem}>
              <span className={cx(styles.legendRule, styles.legendRuleGir)} aria-hidden="true" />
              GIR
            </span>
            <span className={styles.legendItem}>
              <span className={cx(styles.legendRule, styles.legendRuleFw)} aria-hidden="true" />
              Fairway
            </span>
          </>
        ) : undefined
      }
      xLabels={gir.length >= MIN_ROUNDS_FOR_SERIES ? axisMonths(gir) : undefined}
    >
      {!ready ? (
        <ChartSkeleton progress={progress} />
      ) : gir.length < MIN_ROUNDS_FOR_SERIES ? (
        <ChartEmpty
          shape="line"
          requirement={t('stats.needRounds', { count: MIN_ROUNDS_FOR_SERIES })}
          have={t('stats.youHave', { count: gir.length })}
        />
      ) : (
        <LineChart
          // Fixed to the canvas's 0–70 axis: an auto-scaled percentage axis would make a
          // player who went from 11% to 14% look like they had transformed.
          min={0}
          max={70}
          ariaLabel={t('stats.girFairwayAria', {
            girFrom: fmtPct(gir[0]?.value),
            girTo: fmtPct(gir[gir.length - 1]?.value),
            fwFrom: fmtPct(fairway[0]?.value),
            fwTo: fmtPct(fairway[fairway.length - 1]?.value),
          })}
          series={[
            { values: fairway.map((p) => p.value), color: 'var(--dist-par)', strokeWidth: 2.2 },
            { values: gir.map((p) => p.value), color: 'var(--green)', strokeWidth: 2.4 },
          ]}
        />
      )}
    </ChartFrame>
  );
}

// =============================================================== padrões

function DistributionPanel({
  rounds,
  ready,
  progress,
  courseIndex,
}: {
  rounds: Round[];
  ready: boolean;
  progress?: string;
  courseIndex: Map<string, { name: string }> | undefined;
}) {
  const rows = [...rounds].sort(
    (a, b) =>
      new Date(b.completedAt ?? b.startedAt).getTime() -
      new Date(a.completedAt ?? a.startedAt).getTime(),
  );

  return (
    <section className={styles.panel}>
      <h3 className={styles.panelTitle}>{t('stats.distribution')}</h3>
      <p className={styles.panelSubtitle}>{t('stats.distributionSub')}</p>

      {!ready ? (
        <ChartSkeleton progress={progress} />
      ) : (
        <>
          <div className={styles.distRows}>
            {rows.map((r) => (
              <div key={r.id} className={styles.distRow}>
                <span className={styles.distDate}>
                  {fmtDateShort(r.completedAt ?? r.startedAt)}
                </span>
                <span className={styles.distScore}>{fmtInt(r.totalStrokes)}</span>
                <Link
                  to={routes.round(r.id)}
                  className={styles.distBar}
                  aria-label={t('stats.distributionRowAria', {
                    date: fmtDateShort(r.completedAt ?? r.startedAt),
                    course: courseIndex?.get(r.courseId)?.name ?? t('stats.courseFallback'),
                  })}
                >
                  <DistributionBar distribution={r.stats} showLegend={false} />
                </Link>
              </div>
            ))}
          </div>
          <div className={styles.distLegend}>
            <LegendSwatch className={styles.swatchBirdie}>{t('dist.birdies')}</LegendSwatch>
            <LegendSwatch className={styles.swatchPar}>{t('dist.pars')}</LegendSwatch>
            <LegendSwatch className={styles.swatchBogey}>{t('dist.bogeys')}</LegendSwatch>
            <LegendSwatch className={styles.swatchDouble}>{t('dist.doubles')}</LegendSwatch>
          </div>
        </>
      )}
    </section>
  );
}

/* `string | undefined`: CSS Module members are indexed accesses, and the project runs with
   `noUncheckedIndexedAccess`. Narrowing the type is the honest fix; disabling the flag is not. */
function LegendSwatch({
  className,
  children,
}: {
  className: string | undefined;
  children: string;
}) {
  return (
    <span className={styles.legendItem}>
      <span className={cx(styles.legendDot, className)} aria-hidden="true" />
      {children}
    </span>
  );
}

function ByParPanel({
  rounds,
  ready,
  progress,
}: {
  rounds: Round[];
  ready: boolean;
  progress?: string;
}) {
  const byPar = performanceByPar(rounds);
  const worst = parDiagnosis(byPar);

  const bars: Bar[] = byPar.map((p) => ({
    key: `par${p.par}`,
    value: p.averageOverPar,
    display: fmtSignedDecimal(p.averageOverPar),
    label: t('stats.parLabel', { par: p.par }),
    caption: t('stats.parHoles', { count: p.holes }),
    tone: costTone(p.averageOverPar),
  }));

  return (
    <section className={styles.panel}>
      <h3 className={styles.panelTitle}>{t('stats.byPar')}</h3>
      <p className={styles.panelSubtitle}>{t('stats.byParSub')}</p>

      {!ready ? (
        <ChartSkeleton progress={progress} />
      ) : bars.length === 0 ? (
        <ChartEmpty
          requirement={t('stats.needOneRound')}
          have={t('stats.haveNoneInWindow')}
        />
      ) : (
        <>
          <div className={styles.barPad}>
            <BarChart bars={bars} height={132} ariaLabel={t('stats.byParAria')} />
          </div>
          {worst ? (
            <p className={styles.diagnosis}>
              {tx('stats.diagnosis', {
                strong: <strong>{t('stats.diagnosisStrong', { par: worst.par })}</strong>,
                average: fmtSignedDecimal(worst.averageOverPar),
                holes: worst.holes,
              })}
            </p>
          ) : (
            <p className={styles.diagnosis}>{t('stats.noDiagnosis')}</p>
          )}
        </>
      )}
    </section>
  );
}

function ByHolePanel({
  course,
  courseName,
  rounds,
  ready,
  progress,
}: {
  course: import('@/lib/api/types').CourseDetail | undefined;
  courseName: string | null;
  rounds: Round[];
  ready: boolean;
  progress?: string;
}) {
  const stats = useMemo(
    () => (course ? holeStatsFor(course, rounds) : new Map()),
    [course, rounds],
  );

  const here = course ? rounds.filter((r) => r.courseId === course.id) : [];
  const ordered = course ? course.holes.map((h) => stats.get(h.number)).filter(Boolean) : [];
  const enough = here.length >= MIN_ROUNDS_FOR_HOLE_INSIGHT && ordered.length > 0;

  const bars: Bar[] = ordered.map((s) => ({
    key: `h${s.holeNumber}`,
    value: s.averageOverPar,
    display: fmtSignedDecimal(s.averageOverPar),
    label: `${s.holeNumber}`,
    tone: costTone(s.averageOverPar),
  }));

  const insight = enough
    ? worstHoles(ordered.map((s) => ({ holeNumber: s.holeNumber, averageOverPar: s.averageOverPar })))
    : null;

  return (
    <section className={cx(styles.panel, styles.panelWide)}>
      <div className={styles.panelHead}>
        <h3 className={styles.panelTitle}>
          {courseName ? t('stats.byHoleWithCourse', { course: courseName }) : t('stats.byHole')}
        </h3>
        {enough && (
          <span className={styles.panelAside}>
            {t('stats.byHoleAside', { count: here.length })}
          </span>
        )}
      </div>

      {!ready ? (
        <ChartSkeleton progress={progress} />
      ) : !enough ? (
        <ChartEmpty
          requirement={t('stats.needRoundsSameCourse', { count: MIN_ROUNDS_FOR_HOLE_INSIGHT })}
          have={
            courseName
              ? t('stats.haveAtCourse', { course: courseName, count: here.length })
              : t('stats.noCourseWithHistory')
          }
        />
      ) : (
        <>
          <div className={styles.barPad}>
            <BarChart
              bars={bars}
              height={112}
              ariaLabel={t('stats.byHoleAria', {
                course: courseName ?? t('stats.courseFallback'),
              })}
              compact
            />
          </div>
          {insight && (
            <InsightCard
              tone="light"
              sample={t('stats.byHoleSample', { count: here.length })}
            >
              {tx('stats.worstHoles', {
                holes: insight.holes.map((h, i) => (
                  <span key={h.holeNumber}>
                    {i > 0 && t('stats.and')}
                    <strong>{h.holeNumber}</strong>
                  </span>
                )),
                costs: insight.holes.map((h, i) => (
                  <span key={h.holeNumber}>
                    {i > 0 && t('stats.and')}
                    <strong>{fmtSignedDecimal(h.averageOverPar)}</strong>
                  </span>
                )),
                share: fmtPct(insight.share * 100),
              })}
            </InsightCard>
          )}
        </>
      )}
    </section>
  );
}

// ================================================================ helpers

/** "+0,8" / "−0,3" — a per-hole average always carries its sign. */
function fmtSignedDecimal(n: number): string {
  const s = fmtDecimal(Math.abs(n));
  if (n > 0) return `+${s}`;
  if (n < 0) return `−${s}`;
  return fmtDecimal(0);
}

/**
 * "12 voltas de 18 buracos · 3 mar a 9 ago 2026" — o intervalo só entra quando existe, e por isso
 * é uma chave à parte em vez de um pedaço opcional dentro da frase.
 */
function subtitleFor(rounds: RoundSummary[], holes: 9 | 18): string {
  const base = t('stats.subtitle', { count: rounds.length, holes });
  const range = dateRangeLabel(rounds);
  return range ? t('stats.subtitleRange', { base, range }) : base;
}

function bestRound(rounds: RoundSummary[]): RoundSummary | null {
  let best: RoundSummary | null = null;
  for (const r of rounds) {
    if (r.totalStrokes == null) continue;
    if (!best || r.totalStrokes < (best.totalStrokes as number)) best = r;
  }
  return best;
}

/** "nenhuma de 9" — what the hole-count filter is hiding. */
function otherCountCaption(completed: RoundSummary[], active: 9 | 18): string {
  const other = active === 18 ? 9 : 18;
  const n = completed.filter((r) => r.holesPlayed === other).length;
  if (n === 0) return t('stats.noneOfOther', { other });
  return t('stats.moreOfOther', { count: n, other });
}

function CoursePicker({
  courses,
  value,
  onChange,
}: {
  courses: Array<{ id: string; name: string; count: number }>;
  value: string;
  onChange: (id: string) => void;
}) {
  const menu = useMenu<HTMLDivElement>();
  if (courses.length < 2) return null;

  const active = courses.find((c) => c.id === value);

  return (
    <div className={styles.picker} ref={menu.ref}>
      <button
        type="button"
        className={styles.pickerPill}
        onClick={menu.toggle}
        aria-haspopup="listbox"
        aria-expanded={menu.open}
      >
        {active ? active.name : t('rounds.filter.allCourses')}
        <span className={styles.pickerCaret} aria-hidden="true">
          <ChevronDownIcon size={14} />
        </span>
      </button>
      {menu.open && (
        <div className={styles.pickerMenu} role="listbox" onClick={menu.close}>
          <button
            type="button"
            role="option"
            aria-selected={value === ALL_COURSES}
            className={cx(styles.pickerOption, value === ALL_COURSES && styles.pickerOptionActive)}
            onClick={() => onChange(ALL_COURSES)}
          >
            {t('rounds.filter.allCourses')}
          </button>
          {courses.map((c) => (
            <button
              key={c.id}
              type="button"
              role="option"
              aria-selected={value === c.id}
              className={cx(styles.pickerOption, value === c.id && styles.pickerOptionActive)}
              onClick={() => onChange(c.id)}
            >
              {c.name}
              <span className={styles.pickerCount}>{c.count}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function PageSkeleton() {
  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <Skeleton width={220} height={34} />
          <Skeleton width={280} height={12} style={{ marginTop: 10 }} />
        </div>
      </div>
      <div className={styles.summary}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} style={{ flex: 1 }}>
            <Skeleton height={9} width="70%" />
            <Skeleton height={30} style={{ marginTop: 8 }} />
          </div>
        ))}
      </div>
      <div className={styles.grid}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i}>
            <Skeleton width="45%" height={14} />
            <ChartSkeleton />
          </div>
        ))}
      </div>
    </div>
  );
}
